import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../components/ui/sheet';
import PayoutConnectCard from '../components/PayoutConnectCard';
import { formatMoney } from '../lib/currency';
import {
  GraduationCap, Users, BookOpen, Plus, Eye, BarChart3,
  Star, ArrowRight, CheckCircle2, Settings, Upload,
  Trash2, Edit, Play, Pause, ClipboardList, Menu, Link2, LogOut
} from 'lucide-react';


export default function UniversityManagerDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  const [affiliations, setAffiliations] = useState([]);
  const [showAffModal, setShowAffModal] = useState(false);
  const [affForm, setAffForm] = useState({ partner_name: '', partner_type: 'employer', description: '', website: '' });

  useEffect(() => {
    if (user?.user_type !== 'university_manager' && user?.user_type !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'affiliations' && profile?.id) {
      fetchAffiliations();
    }
  }, [activeTab, profile?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const profileRes = await api.get('/universities/my-profile');
      setProfile(profileRes.data);
      setProfileForm(profileRes.data);
    } catch {
      setProfile({});
      setProfileForm({});
    }
    try {
      const coursesRes = await api.get('/courses/provider/dashboard');
      setCourses(coursesRes.data?.courses || coursesRes.data || []);
    } catch {
      try {
        const coursesRes2 = await api.get('/courses?provider=me');
        setCourses(coursesRes2.data?.courses || coursesRes2.data || []);
      } catch {
        setCourses([]);
      }
    }
    setLoading(false);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/universities/my-profile', profileForm);
      toast.success('Profile saved successfully!');
      setProfile(profileForm);
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await api.post('/universities/my-profile/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded!');
    } catch {
      toast.error('Failed to upload logo');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      uploadLogo(file);
    }
  };

  const toggleCourseStatus = async (courseId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/courses/${courseId}`, { status: newStatus });
      setCourses(courses.map(c => c.id === courseId ? { ...c, status: newStatus } : c));
      toast.success(`Course ${newStatus}`);
    } catch {
      toast.error('Failed to update course');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await api.delete(`/courses/${courseId}`);
      setCourses(courses.filter(c => c.id !== courseId));
      toast.success('Course deleted');
    } catch {
      toast.error('Failed to delete course');
    }
  };

  const fetchAffiliations = async () => {
    try {
      const res = await api.get(`/universities/${profile?.id}/affiliations`);
      setAffiliations(res.data || []);
    } catch { setAffiliations([]); }
  };

  const saveAffiliation = async () => {
    try {
      await api.post('/universities/my-profile/affiliations', affForm);
      toast.success('Affiliation added');
      setShowAffModal(false);
      setAffForm({ partner_name: '', partner_type: 'employer', description: '', website: '' });
      fetchAffiliations();
    } catch { toast.error('Failed to add affiliation'); }
  };

  const deleteAffiliation = async (id) => {
    if (!window.confirm('Remove this affiliation?')) return;
    try {
      await api.delete(`/universities/my-profile/affiliations/${id}`);
      toast.success('Removed');
      fetchAffiliations();
    } catch { toast.error('Failed'); }
  };

  const updateApplicationStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
      setApplications(applications.map(a => a.id === appId ? { ...a, status } : a));
      toast.success('Application updated');
    } catch {
      setApplications(applications.map(a => a.id === appId ? { ...a, status } : a));
      toast.success('Status updated');
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: Settings },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'applications', label: 'Applications', icon: ClipboardList },
    { id: 'affiliations', label: 'Affiliations', icon: Link2 },
  ];

  const totalEnrolled = courses.reduce((sum, c) => sum + (c.enrollments_count || 0), 0);
  const pendingApps = applications.filter(a => a.status === 'pending').length;
  const avgRating = profile?.average_rating || 4.5;

  const stats = [
    { label: 'Total Courses', value: courses.length, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Enrolled Students', value: totalEnrolled.toLocaleString(), icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Pending Applications', value: pendingApps, icon: ClipboardList, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'Avg Rating', value: avgRating.toFixed ? avgRating.toFixed(1) : avgRating, icon: Star, color: 'text-purple-600', bg: 'bg-purple-100' },
  ];

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    reviewing: 'bg-blue-100 text-blue-700',
    accepted: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    waitlisted: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-emerald-800 min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">University Manager</p>
                <p className="text-white/60 text-xs truncate max-w-[140px]">{user?.full_name}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
            <div className="mt-8">
              <Link to="/courses/post">
                <Button className="w-full bg-white text-emerald-800 hover:bg-white/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </Link>
              <Button variant="ghost" className="w-full mt-2 text-white/70 hover:text-white hover:bg-white/10" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-emerald-800 border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">University Manager</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="max-w-5xl">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground">University Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                {profile?.name || 'Your institution'} — manage your profile, courses and students
              </p>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <PayoutConnectCard />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {loading ? (
                    Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)
                  ) : (
                    stats.map((s, i) => (
                      <Card key={i}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{s.label}</p>
                              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                              <s.icon className={`w-6 h-6 ${s.color}`} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle>Recent Enrollments</CardTitle></CardHeader>
                    <CardContent>
                      {students.length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-6">No enrollments yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {students.slice(0, 5).map((s) => (
                            <div key={s.id} className="flex items-center justify-between py-2 border-b last:border-0">
                              <div>
                                <p className="text-sm font-medium">{s.name}</p>
                                <p className="text-xs text-muted-foreground">{s.course}</p>
                              </div>
                              <Badge className={s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                                {s.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle>Recent Applications</CardTitle></CardHeader>
                    <CardContent>
                      {applications.length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-6">No applications yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {applications.slice(0, 5).map((a) => (
                            <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                              <div>
                                <p className="text-sm font-medium">{a.applicant_name}</p>
                                <p className="text-xs text-muted-foreground">{a.course}</p>
                              </div>
                              <Badge className={statusColors[a.status] || 'bg-gray-100 text-gray-700'}>
                                {a.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Institution Profile</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Institution Name</Label>
                        <Input
                          value={profileForm.name || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          placeholder="University of ..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <select
                          value={profileForm.type || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, type: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                        >
                          <option value="university">University</option>
                          <option value="college">College</option>
                          <option value="training_provider">Training Provider</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={profileForm.website || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                          placeholder="https://www.example.ac.uk"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={profileForm.email || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          placeholder="info@example.ac.uk"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={profileForm.phone || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                          placeholder="+44 ..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={profileForm.location || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                          placeholder="City, England"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input
                          value={profileForm.country || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                          placeholder="United Kingdom"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Accreditation</Label>
                        <Input
                          value={profileForm.accreditation || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, accreditation: e.target.value })}
                          placeholder="e.g. QAA, Ofsted"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Degree Levels Offered (comma-separated)</Label>
                      <Input
                        value={(profileForm.degree_levels || []).join(', ')}
                        onChange={(e) => setProfileForm({ ...profileForm, degree_levels: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="foundation, undergraduate, postgraduate, phd"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Programmes/Subject Areas (comma-separated)</Label>
                      <Input
                        value={(profileForm.specializations || []).join(', ')}
                        onChange={(e) => setProfileForm({ ...profileForm, specializations: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="Computer Science, Business, Engineering"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Short Description</Label>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none outline-none focus:ring-2 focus:ring-emerald-500/20"
                        rows={3}
                        value={profileForm.description || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                        placeholder="A brief description of your institution..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>About Us</Label>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none outline-none focus:ring-2 focus:ring-emerald-500/20"
                        rows={6}
                        value={profileForm.about_us || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, about_us: e.target.value })}
                        placeholder="Full details about your institution, history, values..."
                      />
                    </div>
                    <Button
                      onClick={saveProfile}
                      disabled={saving}
                      className="bg-emerald-700 hover:bg-emerald-800"
                    >
                      {saving ? 'Saving...' : 'Save Profile'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Logo Upload */}
                <Card>
                  <CardHeader><CardTitle>Institution Logo</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                        ) : (
                          <GraduationCap className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          ref={logoInputRef}
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => logoInputRef.current?.click()}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2">PNG, JPG up to 2MB. Recommended: 400×400px</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">My Courses ({courses.length})</h2>
                  <Link to="/courses/post">
                    <Button className="bg-emerald-700 hover:bg-emerald-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Course
                    </Button>
                  </Link>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                ) : courses.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No courses yet. Add your first course.</p>
                      <Link to="/courses/post">
                        <Button className="bg-emerald-700 hover:bg-emerald-800">Add Course</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {courses.map((course) => (
                      <Card key={course.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold truncate">{course.title}</h3>
                                <Badge className={course.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                                  {course.status}
                                </Badge>
                              </div>
                              <div className="flex gap-4 text-xs text-muted-foreground">
                                <span>{course.level}</span>
                                <span>{course.duration}</span>
                                <span className="font-medium text-foreground">
                                  {course.is_free ? 'Free' : formatMoney(course.price, course.currency)}
                                </span>
                                <span>{course.enrollments_count || 0} enrolled</span>
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleCourseStatus(course.id, course.status)}
                                className={course.status === 'active' ? 'text-amber-600 border-amber-300' : 'text-emerald-600 border-emerald-300'}
                              >
                                {course.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                                <span className="ml-1 hidden sm:inline">{course.status === 'active' ? 'Pause' : 'Activate'}</span>
                              </Button>
                              <Link to={`/courses/${course.id}/edit`}>
                                <Button variant="outline" size="sm">
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-200 hover:bg-red-50"
                                onClick={() => deleteCourse(course.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Enrolled Students ({students.length})</h2>
                <Card>
                  <CardContent className="p-0">
                    {students.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No students enrolled yet.</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {students.map((s) => (
                          <div key={s.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                                {s.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{s.name}</p>
                                <p className="text-xs text-muted-foreground">{s.course}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground hidden sm:block">{s.enrolled_at}</span>
                              <Badge className={s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                                {s.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Applications Tab */}
            {activeTab === 'applications' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Student Applications ({applications.length})</h2>
                <div className="space-y-3">
                  {applications.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No applications received yet.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    applications.map((app) => (
                      <Card key={app.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                              <p className="font-semibold">{app.applicant_name}</p>
                              <p className="text-sm text-muted-foreground">{app.course}</p>
                              <p className="text-xs text-muted-foreground">Applied: {app.applied_at}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={statusColors[app.status] || 'bg-gray-100 text-gray-700'}>
                                {app.status}
                              </Badge>
                              <select
                                value={app.status}
                                onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                                className="text-sm border rounded-md px-3 py-1.5 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              >
                                <option value="pending">Pending</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                                <option value="waitlisted">Waitlisted</option>
                              </select>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}
            {/* Affiliations Tab */}
            {activeTab === 'affiliations' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">Affiliations & Partners</h2>
                    <p className="text-muted-foreground mt-1">Manage employer partnerships, accreditations and institutional links</p>
                  </div>
                  <Button onClick={()=>setShowAffModal(true)} className="bg-emerald-700 hover:bg-emerald-800">
                    <Plus className="w-4 h-4 mr-2"/>Add Affiliation
                  </Button>
                </div>

                {affiliations.length === 0 ? (
                  <Card><CardContent className="py-12 text-center">
                    <Link2 className="w-12 h-12 text-muted-foreground mx-auto mb-4"/>
                    <p className="text-muted-foreground">No affiliations yet. Add employer partners, accreditation bodies or sister institutions.</p>
                  </CardContent></Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {affiliations.map(aff => (
                      <Card key={aff.id}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{aff.partner_name}</h3>
                                <Badge variant="outline" className="capitalize text-xs">{aff.partner_type}</Badge>
                              </div>
                              {aff.description && <p className="text-sm text-muted-foreground mb-2">{aff.description}</p>}
                              {aff.website && <a href={aff.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#003366] hover:underline">{aff.website}</a>}
                            </div>
                            <Button variant="ghost" size="icon" onClick={()=>deleteAffiliation(aff.id)} className="text-red-500 shrink-0">
                              <Trash2 className="w-4 h-4"/>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Add Modal */}
                {showAffModal && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setShowAffModal(false)}>
                    <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4" onClick={e=>e.stopPropagation()}>
                      <h3 className="text-lg font-bold">Add Affiliation</h3>
                      <div className="space-y-2"><label className="text-sm font-medium">Partner Name *</label><input className="w-full border rounded-lg px-3 py-2 text-sm" value={affForm.partner_name} onChange={e=>setAffForm(f=>({...f,partner_name:e.target.value}))} placeholder="e.g. Google, NHS, UCAS"/></div>
                      <div className="space-y-2"><label className="text-sm font-medium">Type</label>
                        <select className="w-full border rounded-lg px-3 py-2 text-sm" value={affForm.partner_type} onChange={e=>setAffForm(f=>({...f,partner_type:e.target.value}))}>
                          {['employer','institution','government','accreditation_body','industry_body'].map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2"><label className="text-sm font-medium">Description</label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={affForm.description} onChange={e=>setAffForm(f=>({...f,description:e.target.value}))} placeholder="Brief description of the partnership"/></div>
                      <div className="space-y-2"><label className="text-sm font-medium">Website</label><input className="w-full border rounded-lg px-3 py-2 text-sm" value={affForm.website} onChange={e=>setAffForm(f=>({...f,website:e.target.value}))} placeholder="https://..."/></div>
                      <div className="flex justify-end gap-2">
                        <button onClick={()=>setShowAffModal(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                        <button onClick={saveAffiliation} className="px-4 py-2 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800">Add</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
